/* eslint-disable max-params */
import { PAGE_VIEW } from '@/entities/tracking/const/type';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { EVENT, PAGE_UPDATE } from '@/utils/constants/tracking';

import { appLogger } from './app-logger/appLogger';
import { toError } from './errors/toError';

interface ComponentInfo {
    [key: string]: Record<string, string>;
}

interface DataLayerObject {
    event: string;
    eventInfo: Record<string, string>;
    component?: ComponentInfo;
}

/**
    Calls to the adobeDataLayer, passing forward the different info for tracking purposes
 */
type TrackOptions = {
    /** Skip Tealium page view — caller will fire after subscriber fields are on utag_data. */
    skipPageView?: boolean;
};

function firePageUpdate(eventInfo: Record<string, string>, options?: TrackOptions) {
    globalThis.window.adobeDataLayer.push(eventInfo);
    if (options?.skipPageView || !globalThis.window.readDataLayerPageUpdate) {
        return;
    }

    try {
        globalThis.window.readDataLayerPageUpdate();
        // Marked only on success: a throw means no beacon emitted, and whenAll's timeout
        // is the stall safety net. Errors stay swallowed so Tealium can't break the page.
        BootstrapSignals.mark(BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED);
    } catch (error) {
        appLogger.error(
            'Tealium readDataLayerPageUpdate extension threw',
            { component: 'track' },
            toError(error),
        );
    }
}

function fireEvent(name: string, eventInfo: Record<string, string>, component?: ComponentInfo) {
    let dataLayerObject: DataLayerObject = {
        event: name.toString(),
        eventInfo,
    };

    if (component) {
        dataLayerObject = Object.assign(dataLayerObject, component);
    }

    globalThis.window.adobeDataLayer.push(dataLayerObject);
    if (!globalThis.window.readDataLayerEvent) {
        return;
    }

    try {
        globalThis.window.readDataLayerEvent();
    } catch (e) {}
}

export default function track(
    name: string,
    type: string,
    eventInfo: Record<string, string>,
    component?: ComponentInfo,
    options?: TrackOptions,
) {
    if (!globalThis.window?.adobeDataLayer) {
        return;
    }

    // we want to ensure that page view tracking calls are sent out
    // right now type is always sent as EVENT, once dataHub migration is complete
    // this code should rely on type not name
    if (type === PAGE_UPDATE || name === PAGE_VIEW) {
        firePageUpdate(eventInfo, options);
    } else if (type === EVENT) {
        fireEvent(name, eventInfo, component);
    }
}
